import React from 'react'
import { SketchHomeButton, routes } from '@sketch/modules-common'
import { matchPath, useLocation, useRouteMatch } from 'react-router-dom'
import {
  Flex,
  useResponsiveDropdown,
  Dropdown,
  ButtonUnstyled,
  DocumentHead,
  useBreakpoint,
} from '@sketch/components'
import { GetPreviewDetailsQuery } from '@sketch/gql-types'
import { PreviewTitle } from './PageCanvasViewHeader.styles'
import { usePreview } from '../../../views/PreviewView/PreviewContext'
import {
  PAGES_TEXT,
  PROTOTYPES_TEXT,
} from '../../../components/HeaderNavigation/PagesNavigationItem/constants'
import {
  ViewWrapper,
  StyledSeparator,
  DropdownItem,
  PrototypeIcon,
  PageIcon,
  ChevronsIcon,
} from '../../../components/HeaderNavigation/NavigationItem.styles'
import NavigationItem from '../../../components/HeaderNavigation/NavigationItem'
import { BreadcrumbDivider } from '../../../components/HeaderNavigation/Breadcrumbs.styles'

interface ViewsSelectorProps {
  preview: NonNullable<GetPreviewDetailsQuery['preview']> | undefined
  isMobile: boolean
}

const PreviewPagesDropdown: React.FCC<ViewsSelectorProps> = ({
  preview,
  isMobile,
}) => {
  const location = useLocation()

  const copyPerItem = {
    pages: PAGES_TEXT,
    prototypes: PROTOTYPES_TEXT,
  }

  if (!preview) {
    return null
  }

  const previewRoute = routes.PREVIEW_VIEW.create({
    previewID: preview.identifier,
  })
  const prototypeRoute = routes.PREVIEW_PROTOTYPES.create({
    previewID: preview.identifier,
  })

  const hasPrototypes = preview.prototypes.meta.totalCount > 0

  return (
    <ViewWrapper data-testid="views-dropdown">
      {isMobile && <StyledSeparator />}
      {!isMobile && <Dropdown.Header>Views</Dropdown.Header>}
      {!isMobile && (
        <DropdownItem
          key={PAGES_TEXT}
          to={previewRoute}
          isActive={() =>
            !!matchPath(location.pathname, {
              path: previewRoute,
              exact: true,
            })
          }
        >
          <PageIcon />
          {copyPerItem['pages']}
        </DropdownItem>
      )}

      {hasPrototypes && (
        <DropdownItem to={prototypeRoute}>
          <PrototypeIcon />
          {copyPerItem['prototypes']}
        </DropdownItem>
      )}
    </ViewWrapper>
  )
}

export const PreviewHeader = () => {
  const previewContext = usePreview()
  const isMobile = !useBreakpoint('xs')

  // Check if we're in a preview prototype route using useRouteMatch
  const isPrototypeRoute = !!useRouteMatch({
    path: routes.PREVIEW_PROTOTYPES.template(),
    exact: true,
  })

  const [content, buttonProps] = useResponsiveDropdown({
    dropdown: PreviewPagesDropdown,
    dropdownProps: {
      preview: previewContext?.preview,
      isMobile,
    },
    dropdownStyle: {
      maxHeight: '80vh',
      overflowY: 'auto',
    },
    usePortal: true,
  })

  if (!previewContext) {
    return null
  }

  const preview = previewContext.preview
  const routeLabel = isPrototypeRoute ? 'Prototypes' : 'Pages'

  return (
    <>
      <DocumentHead title={`${preview.name} - ${routeLabel}`} />
      <Flex alignItems="center">
        <SketchHomeButton />
        <PreviewTitle>{preview.name}</PreviewTitle>
        <BreadcrumbDivider />
        <ButtonUnstyled data-testid="view-selector" {...buttonProps}>
          <NavigationItem
            icon={isPrototypeRoute ? 'prototype' : 'page'}
            label={routeLabel}
          />
          <ChevronsIcon />
        </ButtonUnstyled>
        {content}
      </Flex>
    </>
  )
}
